<template>
  <v-dialog
    v-model="dialog"
    :max-width="options.width"
    :style="{ zIndex: options.zIndex }"
    @keydown.esc="cancel"
  >
    <v-card>
      <v-container
        :class="{
          'px-10': !mobile,
          'pt-8': !mobile,
          'px-6': mobile,
          'pt-4': mobile
        }"
      >
        <p class="subtitle-1 font-weight-bold mb-2">Generate Report</p>
        <v-form v-model="isFormValid">
          <v-row>
            <v-col class="py-0" cols="12" sm="12" md="12">
              <v-select
                v-model="selectedTenants"
                :items="
                  tenants.filter((t) =>
                    t.text.toLowerCase().includes(searchTenant.toLowerCase())
                  )
                "
                label="Select Tenant"
                :rules="tenantRules"
                outlined
                dense
                multiple
                chips
                small-chips
              >
                <template v-slot:prepend-item>
                  <v-list-item ripple>
                    <v-list-item-content>
                      <v-text-field
                        v-model="searchTenant"
                        outlined
                        dense
                        hide-details
                        placeholder="Search Tenant"
                      ></v-text-field>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item ripple @click="selectAllTenants">
                    <v-list-item-action>
                      <v-icon
                        :color="
                          selectedTenants.length > 0 ? 'indigo darken-4' : ''
                        "
                        >{{ iconTenants }}</v-icon
                      >
                    </v-list-item-action>
                    <v-list-item-content>
                      <v-list-item-title>Select All</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-divider class="mt-2"></v-divider>
                </template>
                <template v-slot:selection="{ item, index }">
                  <v-chip v-if="index === 0" small>
                    <span>{{ item.text }}</span>
                  </v-chip>
                  <v-chip v-if="index === 1" small>
                    <span>{{ item.text }}</span>
                  </v-chip>
                  <v-chip v-if="index === 2" small>
                    <span>{{ item.text }}</span>
                  </v-chip>
                  <span v-if="index === 3" class="grey--text caption"
                    >(+{{ selectedTenants.length - 3 }} others)</span
                  >
                </template>
                <template v-slot:prepend-inner>
                  <v-icon small class="mt-1">mdi-account</v-icon>
                </template>
              </v-select>
            </v-col>
          </v-row>
          <v-row>
            <v-col class="py-0" cols="12" sm="12" md="12">
              <v-select
                v-model="selectedCategories"
                :items="paymentCategories"
                label="Select Category"
                :rules="categoryRules"
                multiple
                dense
                outlined
              >
                <template v-slot:prepend-item>
                  <v-list-item ripple @click="selectAllCategories">
                    <v-list-item-action>
                      <v-icon
                        :color="
                          selectedCategories.length > 0 ? 'indigo darken-4' : ''
                        "
                        >{{
                          iconSelected(
                            allCategoriesSelected,
                            someCategoriesSelected
                          )
                        }}</v-icon
                      >
                    </v-list-item-action>
                    <v-list-item-content>
                      <v-list-item-title>Select All</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-divider class="mt-2"></v-divider>
                </template>
                <template v-slot:prepend-inner>
                  <v-icon small class="mt-1">mdi-tools</v-icon>
                </template>
              </v-select>
            </v-col>
          </v-row>
          <v-row>
            <v-col class="py-0" cols="12" sm="12" md="12">
              <v-select
                v-model="selectedStatuses"
                :items="statuses"
                label="Select Status"
                :rules="statusRules"
                multiple
                outlined
                dense
              >
                <template v-slot:prepend-item>
                  <v-list-item ripple @click="selectAllStatuses">
                    <v-list-item-action>
                      <v-icon
                        :color="
                          selectedStatuses.length > 0 ? 'indigo darken-4' : ''
                        "
                        >{{
                          iconSelected(
                            allStatusesSelected,
                            someStatusesSelected
                          )
                        }}</v-icon
                      >
                    </v-list-item-action>
                    <v-list-item-content>
                      <v-list-item-title>Select All</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-divider class="mt-2"></v-divider>
                </template>
                <template v-slot:prepend-inner>
                  <v-icon small class="mt-1">mdi-check-circle</v-icon>
                </template>
              </v-select>
            </v-col>
          </v-row>
          <v-row>
            <v-col class="py-0" cols="12" sm="12" md="12">
              <v-select
                v-model="selectedPaymentTypes"
                :items="paymentTypes"
                label="Optional: Select Payment Type"
                multiple
                dense
                outlined
              >
                <template v-slot:prepend-item>
                  <v-list-item ripple @click="selectAllPaymentTypes">
                    <v-list-item-action>
                      <v-icon
                        :color="
                          selectedPaymentTypes.length > 0
                            ? 'indigo darken-4'
                            : ''
                        "
                        >{{
                          iconSelected(
                            allPaymentTypesSelected,
                            somePaymentTypesSelected
                          )
                        }}</v-icon
                      >
                    </v-list-item-action>
                    <v-list-item-content>
                      <v-list-item-title>Select All</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-divider class="mt-2"></v-divider>
                </template>
                <template v-slot:prepend-inner>
                  <v-icon small class="mt-1">mdi-tag</v-icon>
                </template>
              </v-select>
            </v-col>
          </v-row>
          <v-row>
            <v-col class="py-0" cols="12" sm="12" md="12">
              <v-select
                v-model="selectedTags"
                :items="propertyTags"
                label="Optional: Select Tag"
                multiple
                dense
                outlined
              >
                <template v-slot:prepend-item>
                  <v-list-item ripple @click="selectAllTags">
                    <v-list-item-action>
                      <v-icon
                        :color="
                          selectedTags.length > 0 ? 'indigo darken-4' : ''
                        "
                        >{{
                          iconSelected(allTagsSelected, someTagsSelected)
                        }}</v-icon
                      >
                    </v-list-item-action>
                    <v-list-item-content>
                      <v-list-item-title>Select All</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-divider class="mt-2"></v-divider>
                </template>
                <template v-slot:prepend-inner>
                  <v-icon small class="mt-1">mdi-tag</v-icon>
                </template>
              </v-select>
            </v-col>
          </v-row>
          <v-row>
            <v-col class="py-0" cols="12" sm="12" md="12">
              <p class="caption font-weight-bold">Date Created</p>
            </v-col>
            <v-col class="pt-0" cols="12" sm="12" md="12">
              <v-checkbox
                v-model="created.allDates"
                class="mt-0"
                label="All Dates"
                hide-details
              ></v-checkbox>
            </v-col>
            <v-col class="py-0" cols="12" sm="12" md="6">
              <v-menu
                v-model="created.startDateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
                :disabled="created.allDates"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="created.startDate"
                    label="Start Date"
                    v-bind="attrs"
                    :rules="startDateRules"
                    :disabled="created.allDates"
                    readonly
                    outlined
                    dense
                    v-on="on"
                  >
                    <template v-slot:prepend-inner>
                      <v-icon small class="mt-1">mdi-calendar-month</v-icon>
                    </template>
                  </v-text-field>
                </template>
                <v-date-picker
                  v-model="created.startDate"
                  no-title
                  scrollable
                  @input="created.startDateMenu = false"
                >
                </v-date-picker>
              </v-menu>
            </v-col>
            <v-col class="py-0" cols="12" sm="12" md="6">
              <v-menu
                v-model="created.endDateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
                :disabled="created.allDates"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="created.endDate"
                    label="End Date"
                    prepend-inner-icon="mdi-calendar-month"
                    v-bind="attrs"
                    :rules="createdEndDateRules"
                    :disabled="created.allDates"
                    readonly
                    outlined
                    dense
                    v-on="on"
                  >
                    <template v-slot:prepend-inner>
                      <v-icon small class="mt-1">mdi-calendar-month</v-icon>
                    </template>
                  </v-text-field>
                </template>
                <v-date-picker
                  v-model="created.endDate"
                  no-title
                  scrollable
                  @input="created.endDateMenu = false"
                >
                </v-date-picker>
              </v-menu>
            </v-col>
          </v-row>
          <v-row>
            <v-col class="py-0" cols="12" sm="12" md="12">
              <p class="caption font-weight-bold">Date Paid</p>
            </v-col>
            <v-col class="pt-0" cols="12" sm="12" md="12">
              <v-checkbox
                v-model="paid.allDates"
                class="mt-0"
                label="All Dates"
                hide-details
              ></v-checkbox>
            </v-col>
            <v-col class="py-0" cols="12" sm="12" md="6">
              <v-menu
                v-model="paid.startDateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
                :disabled="paid.allDates"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="paid.startDate"
                    label="Start Date"
                    v-bind="attrs"
                    :rules="startDateRules"
                    :disabled="paid.allDates"
                    readonly
                    outlined
                    dense
                    v-on="on"
                  >
                    <template v-slot:prepend-inner>
                      <v-icon small class="mt-1">mdi-calendar-month</v-icon>
                    </template>
                  </v-text-field>
                </template>
                <v-date-picker
                  v-model="paid.startDate"
                  no-title
                  scrollable
                  @input="paid.startDateMenu = false"
                >
                </v-date-picker>
              </v-menu>
            </v-col>
            <v-col class="py-0" cols="12" sm="12" md="6">
              <v-menu
                v-model="paid.endDateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
                :disabled="paid.allDates"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="paid.endDate"
                    label="End Date"
                    v-bind="attrs"
                    :rules="paidEndDateRules"
                    :disabled="paid.allDates"
                    readonly
                    outlined
                    dense
                    v-on="on"
                  >
                    <template v-slot:prepend-inner>
                      <v-icon small class="mt-1">mdi-calendar-month</v-icon>
                    </template>
                  </v-text-field>
                </template>
                <v-date-picker
                  v-model="paid.endDate"
                  no-title
                  scrollable
                  @input="paid.endDateMenu = false"
                >
                </v-date-picker>
              </v-menu>
            </v-col>
          </v-row>
        </v-form>
        <v-card-actions
          :class="{
            'd-flex': mobile,
            'flex-column': mobile
          }"
        >
          <v-spacer></v-spacer>
          <v-item-group
            dense
            borderless
            class="
              v-btn-toggle v-btn-toggle--borderless v-btn-toggle--dense
              mr-2
            "
          >
            <v-btn
              class="font-weight-bold"
              color="primary darken-1"
              :disabled="!isFormValid"
              @click="finish"
            >
              Download File
            </v-btn>
            <v-menu offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  color="primary darken-1"
                  :disabled="!isFormValid"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon color="white">mdi-chevron-down</v-icon>
                </v-btn>
              </template>
              <v-list dense>
                <v-list-item
                  v-for="(item, index) in downloadFileTypes"
                  :key="index"
                  @click="item.action"
                >
                  <v-list-item-icon class="mr-2">
                    <v-icon small>{{ item.icon }}</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>{{ item.title }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </v-item-group>
          <v-btn
            class="font-weight-bold"
            color="grey darken-1"
            text
            @click="cancel"
            >Cancel</v-btn
          >
        </v-card-actions>
      </v-container>
    </v-card>
    <v-overlay :value="loading" absolute>
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </v-dialog>
</template>

<script>
import { dayjs } from '../../util/date'
import { paymentStatusSelect, PaymentTypesSelect } from '../../config/payment'

export default {
  data() {
    return {
      dialog: false,
      options: {
        color: 'primary',
        width: 500,
        zIndex: 200
      },
      isFormValid: true,
      created: {
        startDate: dayjs().startOf('day').format('YYYY-MM-DD'),
        endDate: dayjs().startOf('day').format('YYYY-MM-DD'),
        startDateMenu: false,
        endDateMenu: false,
        allDates: false
      },
      paid: {
        startDate: dayjs().startOf('day').format('YYYY-MM-DD'),
        endDate: dayjs().startOf('day').format('YYYY-MM-DD'),
        // startDatePicker: dayjs().hour(0).minute(0).second(0).millisecond(0).format('YYYY-MM-DD'),
        // endDatePicker: dayjs().hour(0).minute(0).second(0).millisecond(0).format('YYYY-MM-DD'),
        startDateMenu: false,
        endDateMenu: false,
        allDates: false
      },
      tenantRules: [(v) => v.length > 0 || 'Please select a tenant'],
      categoryRules: [(v) => v.length > 0 || 'Please select a category'],
      statusRules: [(v) => v.length > 0 || 'Please select a status'],
      startDateRules: [(v) => v.length > 0 || 'Please choose a start date'],
      createdEndDateRules: [
        (v) => v.length > 0 || 'Please choose an end date',
        (v) =>
          dayjs(v).isSameOrAfter(dayjs(this.created.startDate), 'day') ||
          'End Date must be later than start date'
      ],
      paidEndDateRules: [
        (v) => v.length > 0 || 'Please choose an end date',
        (v) =>
          dayjs(v).isSameOrAfter(dayjs(this.paid.startDate), 'day') ||
          'End Date must be later than start date'
      ],
      paymentCategories: [],
      propertyTags: [],
      selectedTags: [],
      selectedCategories: [],
      selectedStatuses: [],
      statuses: paymentStatusSelect,
      selectedTenants: [],
      loading: true,
      searchTenant: '',
      downloadFileTypes: [
        {
          icon: 'mdi-file-excel',
          title: 'Excel File (.xlsx)',
          action: () => this.finish('xlsx')
        },
        {
          icon: 'mdi-file-delimited',
          title: 'Comma separated values (.csv)',
          action: () => this.finish('csv')
        }
      ],
      paymentTypes: PaymentTypesSelect,
      selectedPaymentTypes: []
    }
  },
  computed: {
    tenants() {
      return this.$store.getters['users/getTenants']
        .filter(
          (v, i, a) => a.findIndex((t) => t.username === v.username) === i
        )
        .map((tenant) => {
          return {
            value: tenant.userId,
            text:
              (tenant.alias && tenant.alias.name) || this.getTenantName(tenant)
          }
        })
    },
    allTenantsSelected() {
      return this.selectedTenants.length === this.tenants.length
    },
    someTenantsSelected() {
      return this.selectedTenants.length > 0 && !this.allTenantsSelected
    },
    allCategoriesSelected() {
      return this.selectedCategories.length === this.paymentCategories.length
    },
    someCategoriesSelected() {
      return this.selectedCategories.length > 0 && !this.allCategoriesSelected
    },
    allTagsSelected() {
      return this.selectedTags.length === this.propertyTags.length
    },
    someTagsSelected() {
      return this.selectedTags.length > 0 && !this.allTagsSelected
    },
    allStatusesSelected() {
      return this.selectedStatuses.length === this.statuses.length
    },
    someStatusesSelected() {
      return this.selectedStatuses.length > 0 && !this.allStatusesSelected
    },
    iconTenants() {
      if (this.allTenantsSelected) return 'mdi-close-box'
      if (this.someTenantsSelected) return 'mdi-minus-box'
      return 'mdi-checkbox-blank-outline'
    },
    mobile() {
      return this.$vuetify.breakpoint.xs
    },
    allPaymentTypesSelected() {
      return this.selectedPaymentTypes.length === this.paymentTypes.length
    },
    somePaymentTypesSelected() {
      return (
        this.selectedPaymentTypes.length > 0 && !this.allPaymentTypesSelected
      )
    }
  },
  methods: {
    async open(params) {
      this.dialog = true
      const tenantId = params && params.tenantId
      const property = this.$store.getters['properties/getProperty']
      this.propertyTags = property.tags
      this.paymentCategories = property.payment_categories_details[0].categories
      this.paymentCategories.push({ text: 'Deleted', value: 'deleted' })
      await this.$store.dispatch('users/fetchTenantsAction')
      if (Number.isInteger(tenantId)) {
        this.selectedTenants = [tenantId]
      }

      this.loading = false
    },
    getTenantName(tenant) {
      let displayName = ''
      if (tenant.firstName) {
        displayName += tenant.firstName + ' '
        if (tenant.lastName) {
          displayName += tenant.lastName + ' '
        }
      } else {
        return (displayName = tenant.username)
      }

      return displayName
    },
    finish(fileType = 'xlsx') {
      this.$store
        .dispatch('payments/generateReportAction', {
          tenantIds: this.selectedTenants,
          categories: this.selectedCategories,
          tags: this.selectedTags,
          statuses: this.selectedStatuses,
          paymentTypes: this.selectedPaymentTypes,
          createdStartDate: dayjs(this.created.startDate).format(),
          createdEndDate: dayjs(this.created.endDate).format(),
          createdAllDates: this.created.allDates,
          paidStartDate: dayjs(this.paid.startDate).format(),
          paidEndDate: dayjs(this.paid.endDate).format(),
          paidAllDates: this.paid.allDates,
          fileType: fileType === 'csv' ? 'csv' : 'xlsx'
        })
        .then(() => {
          this.dialog = false
        })
    },
    cancel() {
      this.dialog = false
    },
    selectAllTenants() {
      this.$nextTick(() => {
        if (this.allTenantsSelected) {
          this.selectedTenants = []
        } else {
          this.selectedTenants = this.tenants.slice().map((t) => t.value)
        }
      })
    },
    selectAllCategories() {
      this.$nextTick(() => {
        if (this.allCategoriesSelected) {
          this.selectedCategories = []
        } else {
          this.selectedCategories = this.paymentCategories
            .slice()
            .map((t) => t.value)
        }
      })
    },
    selectAllTags() {
      this.$nextTick(() => {
        if (this.allTagsSelected) {
          this.selectedTags = []
        } else {
          this.selectedTags = this.propertyTags.slice().map((t) => t.value)
        }
      })
    },
    selectAllStatuses() {
      this.$nextTick(() => {
        if (this.allStatusesSelected) {
          this.selectedStatuses = []
        } else {
          this.selectedStatuses = this.statuses.slice().map((t) => t.value)
        }
      })
    },
    iconSelected(all, some) {
      if (all) return 'mdi-close-box'
      if (some) return 'mdi-minus-box'
      return 'mdi-checkbox-blank-outline'
    },
    selectAllPaymentTypes() {
      this.$nextTick(() => {
        if (this.allPaymentTypesSelected) {
          this.selectedPaymentTypes = []
        } else {
          this.selectedPaymentTypes = this.paymentTypes
            .slice()
            .map((t) => t.value)
        }
      })
    }
  }
}
</script>

<style scoped>
p {
  margin-bottom: 0;
}
</style>
